import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { history } from '../../';
import { Formik } from 'formik';
import Typography from '@material-ui/core/Typography';

import { useTranslation } from 'react-i18next';
import { isWidthDown, isWidthUp } from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import RefreshIcon from '@material-ui/icons/Refresh';

import pushToGTM, { clickCalculateDesignCalcTypeGA4Tag, OPEN_DESIGN_BY_GA4_TAG } from '../../utils/analytics/gtm';
import { useStyles } from './styles';
import { tabRoutes } from '../../routes';
import validationSchema from './validationSchema';
import { parseInputParameters, handleReset, checkForChanges, areParamsValidDesignParams } from './utils';
import Button from '../Button';
import Input from './Input';
import Output from './Output';
import DesignParameters from './DesignParameters';
import tabbarSlice from '../Tabbar/tabbarSlice';
import designListSlice from '../../redux/slices/designListSlice';
import selectedDesignSlice from '../../redux/slices/selectedDesignSlice';
import { adobeCalculateDesign } from '../../utils/analytics/adobe/adob';
import { localLogCalculateDesigns, localLogOpenDesignByLink } from '../../utils/analytics/local';

const InputsForm = ({ screenWidth, search }) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const ref = useRef(null);
	const inputParametersDL = useSelector(state => state.designList.inputParameters);
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(tabbarSlice.actions.setProgressActive(false));
		if (Object.keys(search).length && areParamsValidDesignParams(search)) {
			const inputParameters = parseInputParameters(search);
			dispatch(designListSlice.actions.setInputParameters(inputParameters));
			dispatch(selectedDesignSlice.actions.setIcPartNumber(search.ic));
			dispatch(selectedDesignSlice.actions.setTopology(search.topology));
			dispatch(tabbarSlice.actions.enableDesignTabs());
			dispatch(selectedDesignSlice.actions.setFetchDesignData(true));
			localLogOpenDesignByLink(search.ic, inputParameters);
			pushToGTM(OPEN_DESIGN_BY_GA4_TAG);
			history.push(tabRoutes.find(route => route.name === 'Summary').path);
		}
	}, [dispatch, search]);

	const handleFormSubmit = values => {
		const newInputValues = parseInputParameters(values);
		localLogCalculateDesigns(newInputValues);
		adobeCalculateDesign(newInputValues);
		pushToGTM(clickCalculateDesignCalcTypeGA4Tag(values.vinNom));

		dispatch(designListSlice.actions.setInputParameters(newInputValues));
		dispatch(tabbarSlice.actions.enableSelectTab());
		dispatch(designListSlice.actions.setFetchDesignListData(true));

		history.push(tabRoutes.find(route => route.name === 'Select').path);
	};

	return (
		<Formik
			innerRef={ref}
			initialValues={{
				vinMin: inputParametersDL.vinMin,
				vinMax: inputParametersDL.vinMax,
				vinNom: inputParametersDL.vinNom,
				vout: inputParametersDL.vout,
				iout: inputParametersDL.iout,
				ambientTemperature: inputParametersDL.ambientTemperature,
				supplyType: inputParametersDL.supplyType,
				lineFrequency: inputParametersDL.lineFrequency,
				isolated: inputParametersDL.isolated,
				automotiveDesign: inputParametersDL.automotiveDesign
			}}
			onSubmit={(values, { setSubmitting }) => {
				setSubmitting(false);
				handleFormSubmit(values);
			}}
			validationSchema={validationSchema}>
			{props => {
				const { values, isSubmitting, setValues, handleSubmit, errors } = props;
				// https://stackoverflow.com/questions/64774988/formik-validation-issubmitting-isvalidating-not-getting-set-to-true
				const isValid = !Object.keys(errors).length;
				return (
					<form onSubmit={handleSubmit} className={classes.form}>
						<Grid item container direction="column" spacing={isWidthDown('sm', screenWidth) ? 2 : 3}>
							<Grid
								item
								container
								direction={isWidthUp('md', screenWidth) ? 'row' : 'column'}
								spacing={isWidthDown('sm', screenWidth) ? 2 : 3}>
								<Grid item xs={isWidthUp('md', screenWidth) ? 6 : 12}>
									<Input screenWidth={screenWidth} formikProps={props} />
								</Grid>

								<Grid item xs={isWidthUp('md', screenWidth) ? 6 : 12}>
									<Output screenWidth={screenWidth} formikProps={props} />
								</Grid>
							</Grid>

							<Grid item container>
								<DesignParameters formikProps={props} />
							</Grid>

							<Grid item container direction="row" justify="space-between">
								<Grid item>
									<Button
										color="secondary"
										bold
										disabled={checkForChanges(isValid, values)}
										onClick={() => handleReset(setValues)}
										startIcon={<RefreshIcon />}
										className={classes.resetButton}>
										<Typography>{t('components.inputs.reset')}</Typography>
									</Button>
								</Grid>

								<Grid item>
									<Button
										id="InputsForm__calculateDesigns_button"
										onClick={() => localLogCalculateDesigns}
										color="primary"
										variant="contained"
										disabled={!isValid || isSubmitting}
										type="submit"
										className={classes.calculateDesignsButton}>
										<Typography>{t('components.inputs.calculateDesigns')}</Typography>
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</form>
				);
			}}
		</Formik>
	);
};

InputsForm.propTypes = {
	screenWidth: PropTypes.number.isRequired,
	search: PropTypes.object
};

export default InputsForm;
